import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AccountService } from '@frontendlib/common/services/account.service';
import { LocalStorageItem } from '@frontendlib/common/decorators/local-storage.decorator';
import type { PaymentReceiptDto } from '@sharedlib/dtos/dto/accounts/billing-quote.dto';
import { distinctUntilChanged, firstValueFrom, map, of, switchMap, type Observable } from 'rxjs';
import { PlanChangeService } from './plan-change.service';

export type PaymentReceiptKind = 'upgrade' | 'credits' | 'payment';

const KINDS: ReadonlyArray<PaymentReceiptKind> = ['upgrade', 'credits', 'payment'];

/** A settled payment, as the Billing success banner shows it. */
export interface PaymentReceipt {
  readonly kind: PaymentReceiptKind;
  readonly title: string;
  /** Tax included. */
  readonly amountCents: number;
  readonly currency: string;
  readonly publicUrl: string | null;
  readonly invoiceUid: string;
  /** ISO time the provider settled the payment. */
  readonly paidAt: string;
}

export interface PaymentReceiptDetail {
  readonly planTitle?: string;
  readonly credits?: number;
}

/** A payment the customer left the app to authenticate, to record once the bank's return confirms it. */
export interface AwaitedReceipt {
  /** ISO time the payment started, as {@link PaymentReceiptService.capture} gives it. */
  readonly since: string;
  readonly kind: PaymentReceiptKind;
  readonly detail: PaymentReceiptDetail;
}

const STORAGE_KEY = 'billing.paymentReceipt';

/** How long after the payment its banner still shows. */
export const RECEIPT_LIFETIME_MS = 24 * 60 * 60 * 1000;

/** The last settled payment's banner, kept in `sessionStorage` per account until dismissed or a day old. */
@Injectable({ providedIn: 'root' })
export class PaymentReceiptService {
  private readonly accountService = inject(AccountService);
  private readonly planChange = inject(PlanChangeService);
  private readonly locale = inject(LOCALE_ID);

  private readonly stored = new LocalStorageItem<PaymentReceipt | null>(STORAGE_KEY, {
    storage: 'session',
    perAccount: false,
    parse: ({ raw }) => (isPaymentReceipt(raw) ? raw : null),
  });

  private readonly accountId = toSignal(this.accountService.getAccount().pipe(map((account) => account.id ?? null)), {
    initialValue: null,
  });

  public readonly current = toSignal(
    this.accountService.getAccount().pipe(
      map((account) => account.id),
      distinctUntilChanged(),
      switchMap((accountId) => (accountId ? this.watch(accountId) : of(null))),
    ),
    { initialValue: null },
  );

  /**
   * The moment a payment starts, to pass to {@link recordLatest} once it settles. Clears the banner, so
   * an earlier payment's never sits beside the outcome of this one.
   */
  public capture(): string {
    this.dismiss();

    return new Date().toISOString();
  }

  /** Looks up the payment settled since `since` and records it; never fails, since the charge already happened. */
  public async recordLatest(since: string, kind: PaymentReceiptKind, detail: PaymentReceiptDetail = {}): Promise<void> {
    const receipt = await firstValueFrom(this.planChange.latestPayment(since), { defaultValue: null }).catch(
      (error: unknown) => {
        console.warn('[PaymentReceiptService] Could not read the latest payment', error);
        return null;
      },
    );
    if (receipt) {
      this.record(receipt, kind, detail);
    }
  }

  public record(receipt: PaymentReceiptDto, kind: PaymentReceiptKind, detail: PaymentReceiptDetail = {}): void {
    const accountId = this.accountId();
    if (!accountId) {
      return;
    }
    this.write(accountId, {
      kind,
      title: this.titleFor(kind, detail),
      amountCents: receipt.paidAmountCents,
      currency: receipt.currency,
      publicUrl: receipt.publicUrl,
      invoiceUid: receipt.invoiceUid,
      paidAt: paidAtOf(receipt),
    });
  }

  /** Clears the stored receipt once it is {@link RECEIPT_LIFETIME_MS} old. */
  public prune(): void {
    const accountId = this.accountId();
    if (accountId) {
      this.pruneFor(accountId);
    }
  }

  public dismiss(): void {
    const accountId = this.accountId();
    if (accountId) {
      this.write(accountId, null);
    }
  }

  private titleFor(kind: PaymentReceiptKind, { planTitle, credits }: PaymentReceiptDetail): string {
    if (kind === 'upgrade' && planTitle) {
      return `You're on ${planTitle}`;
    }
    if (kind === 'credits') {
      return credits == null
        ? 'Credits purchased'
        : `${new Intl.NumberFormat(this.locale).format(credits)} credits purchased`;
    }

    return 'Payment received';
  }

  private pruneFor(accountId: string): void {
    try {
      const receipt = this.stored.getSync(accountId);
      if (receipt && isExpired(receipt)) {
        this.write(accountId, null);
      }
    } catch (error: unknown) {
      console.warn('[PaymentReceiptService] Could not read the receipt', error);
    }
  }

  private watch(accountId: string): Observable<PaymentReceipt | null> {
    this.pruneFor(accountId);
    try {
      return this.stored.get(accountId).pipe(map((value) => (value && !isExpired(value) ? value : null)));
    } catch (error: unknown) {
      console.warn('[PaymentReceiptService] Could not read the receipt', error);
      return of(null);
    }
  }

  private write(accountId: string, receipt: PaymentReceipt | null): void {
    try {
      this.stored.set(receipt, accountId);
    } catch (error: unknown) {
      console.warn('[PaymentReceiptService] Could not store the receipt', error);
    }
  }
}

function isPaymentReceipt(raw: unknown): raw is PaymentReceipt {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    'kind' in raw &&
    KINDS.some((kind) => kind === raw.kind) &&
    'title' in raw &&
    typeof raw.title === 'string' &&
    'amountCents' in raw &&
    typeof raw.amountCents === 'number' &&
    'currency' in raw &&
    typeof raw.currency === 'string' &&
    'publicUrl' in raw &&
    (raw.publicUrl === null || typeof raw.publicUrl === 'string') &&
    'invoiceUid' in raw &&
    typeof raw.invoiceUid === 'string' &&
    'paidAt' in raw &&
    typeof raw.paidAt === 'string' &&
    !Number.isNaN(Date.parse(raw.paidAt))
  );
}

// The charge already happened, so a missing time falls back to now rather than losing the banner.
function paidAtOf(receipt: PaymentReceiptDto): string {
  const paidAt = new Date(receipt.paidAt);

  return Number.isNaN(paidAt.getTime()) ? new Date().toISOString() : paidAt.toISOString();
}

function isExpired(receipt: PaymentReceipt): boolean {
  return Date.now() - Date.parse(receipt.paidAt) >= RECEIPT_LIFETIME_MS;
}

export function isAwaitedReceipt(raw: unknown): raw is AwaitedReceipt {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    'since' in raw &&
    typeof raw.since === 'string' &&
    'kind' in raw &&
    KINDS.some((kind) => kind === raw.kind) &&
    'detail' in raw &&
    typeof raw.detail === 'object' &&
    raw.detail !== null &&
    (!('planTitle' in raw.detail) || typeof raw.detail.planTitle === 'string') &&
    (!('credits' in raw.detail) || typeof raw.detail.credits === 'number')
  );
}
